'use client';

import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fade,
  Tooltip,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import isEqual from 'lodash/isEqual';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../../context/EstimatorContext';
import { costCategoryCalculator } from '../../../utils/costCategoryCalculator';
import useFetchSnapshotRowPricing from '../../../hooks/pricingSnapshot/useFetchSnapshotRowPricing';
import useFetchSnapshotPipePricing from '../../../hooks/pricingSnapshot/useFetchSnapshotPipePricing';
import useFetchSnapshotMiscPricing from '../../../hooks/pricingSnapshot/useFetchSnapshotMiscPricing';
import useFetchSnapshotComponentPricing from '../../../hooks/pricingSnapshot/useFetchSnapshotComponentPricing';
import useFetchSnapshotLineFillPricing from '../../../hooks/pricingSnapshot/useFetchSnapshotLineFillPricing';
import useSaveCostCategorySection from '../../../hooks/useSaveCostCategorySection';
import useNotesDialog from '../../../hooks/useNotesDialog';

const initialRows = [
  {
    no: 15,
    account: '141-01-2001',
    category: 'Materials',
    description: 'Line Pipe',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 16,
    account: '141-01-2001',
    category: 'Materials',
    description: 'Heavy Wall Pipe',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 17,
    account: '141-01-2002',
    category: 'Materials',
    description: 'Segmentable Elbows',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 18,
    account: '141-01-2002',
    category: 'Materials',
    description: 'Mainline Valves',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 19,
    account: '141-01-2002',
    category: 'Materials',
    description: 'Tap Valves',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 20,
    account: '141-01-2002',
    category: 'Materials',
    description: 'Launcher/Receiver Set',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 21,
    account: '141-01-2022',
    category: 'Materials',
    description: 'Cathodic Protection',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 22,
    account: '141-01-2022',
    category: 'Materials',
    description: 'Corrosion Program (Chem Pumps, Coupons)',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 23,
    account: '141-01-2023',
    category: 'Materials',
    description: 'Flow/Pressure Control',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 24,
    account: '141-01-2023',
    category: 'Materials',
    description: 'Water (Hydrotest)',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 25,
    account: '141-01-2041',
    category: 'Materials',
    description: 'Pipeline Markers',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 26,
    account: '141-01-2041',
    category: 'Materials',
    description: 'Line Fill',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 27,
    account: '141-01-2051',
    category: 'Materials',
    description: 'Tarrifs',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 28,
    account: '141-01-2052',
    category: 'Materials',
    description: 'Freight',
    pmOverride: '',
    price: '',
    contingencyPercent: '0%',
    contOverridePercent: '',
    contingency: '0%',
    contingencyCost: '$',
    taxRate: 'n/a',
    taxCost: 'n/a',
    baseCost: '$',
    totalPrice: '$',
  },
  {
    no: 29,
    account: '',
    category: '',
    description: 'Materials Total',
    pmOverride: '',
    price: '',
    contingencyPercent: '',
    contOverridePercent: '',
    contingency: '',
    contingencyCost: '',
    taxRate: '',
    taxCost: '',
    baseCost: '$',
    totalPrice: '$',
    isSummary: true,
  },
];

const columnDefs = [
  {
    headerName: 'Notes',
    field: 'notesIcons',
    cellRenderer: (params) => (
      <>
        <Tooltip title="View Note">
          <IconButton onClick={() => params.context.showNoteDialog(params.data, false)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit Note">
          <IconButton onClick={() => params.context.showNoteDialog(params.data, true)}>
            <NoteAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </>
    ),
    flex: 1,
    suppressMenu: true,
    sortable: false,
  },
  { headerName: 'Account', field: 'account', flex: 1 },
  { headerName: 'Category', field: 'category', flex: 1 },
  { headerName: 'Description', field: 'description', flex: 1 },
  {
    headerName: 'PM Override',
    field: 'pmOverride',
    editable: true,
    flex: 1,
    cellStyle: { backgroundColor: '#f0f0f0', color: '#000' },
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Price',
    field: 'price',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: '% Contingency',
    field: 'contingencyPercent',
    flex: 1,
    valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
    editable: false,
  },
  {
    headerName: '% Cont Override',
    field: 'contOverridePercent',
    editable: true,
    flex: 1,
    valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
    cellStyle: { backgroundColor: '#f0f0f0', color: '#000' },
  },
  {
    headerName: 'Contingency Cost',
    field: 'contingencyCost',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Tax Rate',
    field: 'taxRate',
    flex: 1,
    valueFormatter: ({ value }) => (value !== undefined && value !== '' ? `${parseFloat(value)}%` : ''),
  },
  {
    headerName: 'Tax Cost',
    field: 'taxCost',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Base Cost',
    field: 'baseCost',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
  {
    headerName: 'Total Price',
    field: 'totalPrice',
    flex: 1,
    valueFormatter: ({ value }) =>
      value !== undefined && value !== ''
        ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : '',
  },
];

const Materials = () => {
  const { formData, setFormData, setSectionSummaries } = useEstimator();
  const [gridData, setGridData] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const skipEffectRef = useRef(false);

  const { noteDialogOpen, noteValue, setNoteValue, selectedRow, isEditing, showNoteDialog, resetDialog } =
    useNotesDialog();

  const estimateId = formData.estimateId;
  const { rowPricing } = useFetchSnapshotRowPricing(estimateId);
  const { pipePricing } = useFetchSnapshotPipePricing(estimateId);
  const { componentPricing } = useFetchSnapshotComponentPricing(estimateId);
  const { miscPricing } = useFetchSnapshotMiscPricing(estimateId);
  const { lineFillPricing } = useFetchSnapshotLineFillPricing(estimateId);

  const { saveSection, loading, saved } = useSaveCostCategorySection();

  const handleSave = () => {
    const estimateId = formData.id || formData.estimateId;

    const newOverrides = gridData
      .filter((row) => row.no >= 15 && row.no <= 29)
      .map((row) => ({ ...row, rowNumber: row.no }));

    const mergedOverrides = [
      ...(formData.costCategory?.filter((r) => r.rowNumber < 15 || r.rowNumber > 29) || []),
      ...newOverrides,
    ];

    setFormData((prev) => ({
      ...prev,
      costCategory: mergedOverrides,
    }));

    saveSection(estimateId, 'Materials', gridData);
  };

  const handleNoteSave = () => {
    if (!selectedRow) return;
    const trimmed = noteValue.trim();
    const updatedRow = { ...selectedRow, notes: trimmed };

    const updatedData = gridData.map((r) => (r.no === selectedRow.no ? updatedRow : r));

    setGridData(updatedData);
    resetDialog();

    saveSection(estimateId, 'Materials', updatedData);
  };

  useEffect(() => {
    if (skipEffectRef.current) {
      skipEffectRef.current = false;
      return;
    }

    if (!formData?.pipeSpecs || !formData?.quantities) return;

    // Step 1: Recalculate each row
    const updatedRows = initialRows.map((row) => {
      const override = formData.costCategory?.find((r) => r.rowNumber === row.no) || {};

      const rowWithOverrides = {
        ...row,
        pmOverride: override.pmOverride?.toString() ?? '',
        contOverridePercent: override.contOverridePercent?.toString() ?? '',
        notes: override.notes ?? '',
      };

      const calculator = costCategoryCalculator[row.no];
      if (calculator) {
        const calculated = calculator(
          rowWithOverrides,
          formData,
          rowPricing,
          componentPricing,
          miscPricing,
          lineFillPricing
        );
        return { ...rowWithOverrides, ...calculated };
      }

      return rowWithOverrides;
    });

    // Step 2: Calculate Section 2 summary (rows 15–28)
    let section2Contingency = 0;
    let section2Base = 0;
    let section2Total = 0;
    let section2Tax = 0;

    for (const row of updatedRows) {
      if (row.no >= 15 && row.no <= 28) {
        section2Contingency += isNaN(parseFloat(row.contingencyCost)) ? 0 : parseFloat(row.contingencyCost);
        section2Base += isNaN(parseFloat(row.baseCost)) ? 0 : parseFloat(row.baseCost);
        section2Tax += isNaN(parseFloat(row.taxCost)) ? 0 : parseFloat(row.taxCost);
        section2Total += isNaN(parseFloat(row.totalPrice)) ? 0 : parseFloat(row.totalPrice);
      }
    }

    const summary2Index = updatedRows.findIndex((row) => row.no === 29);
    if (summary2Index !== -1) {
      updatedRows[summary2Index] = {
        ...updatedRows[summary2Index],
        contingencyCost: section2Contingency,
        baseCost: section2Base,
        taxCost: section2Tax,
        totalPrice: section2Total, // Add tax to the total
        isSummary: true,
      };
    }

    // ✅ Prevent unnecessary updates to context
    const newSummary = {
      contingencyCost: section2Contingency,
      baseCost: section2Base,
      taxCost: section2Tax,
      totalPrice: section2Total,
    };

    setSectionSummaries((prev) => {
      if (isEqual(prev[29], newSummary)) return prev;
      return {
        ...prev,
        29: newSummary,
      };
    });

    // Step 3: Set grid data
    setGridData(updatedRows);
  }, [
    pipePricing,
    formData.pipeSpecs,
    componentPricing,
    formData.quantities,
    formData.costCategory,
    miscPricing,
    lineFillPricing,
    formData.costAssumptions,
  ]);

  return (
    <Fade in>
      <Box mt={4} display="flex" flexDirection="column" gap={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            backgroundColor: '#8CA3C4',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6">Cost Category Summary: Materials</Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white' }}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? 'Expand' : 'Collapse'}
          </Button>
        </Box>

        {!isCollapsed && (
          <Paper elevation={3} sx={{ p: 3, backgroundColor: '#F9F9F9', borderRadius: 2 }}>
            <Box
              className="ag-theme-alpine"
              sx={{
                width: '100%',
                overflowX: 'auto',
                paddingBottom: 1,
              }}
            >
              <AgGridReact
                rowData={gridData}
                columnDefs={columnDefs}
                context={{ showNoteDialog }}
                defaultColDef={{
                  resizable: true,
                  editable: true,
                  minWidth: 120,
                  flex: 1,
                }}
                getRowStyle={(params) => {
                  const rowNo = params.data?.no;
                  const hasNote = params.data?.notes && params.data.notes.trim() !== '';

                  // Style for summary row (row 29)
                  if (rowNo === 29) {
                    return {
                      backgroundColor: '#e3f2fd', // light blue
                      fontWeight: 'bold',
                    };
                  }

                  // Style for rows with notes
                  return {
                    backgroundColor: hasNote ? '#fffbe6' : undefined,
                    fontWeight: params.data?.isSummary ? 'bold' : undefined,
                  };
                }}
                domLayout="autoHeight"
                onCellValueChanged={(params) => {
                  const { colDef, data, newValue } = params;
                  const field = colDef.field;

                  if (field === 'pmOverride' || field === 'contOverridePercent') {
                    const updatedCostCategory = [...(formData.costCategory || [])];
                    const index = updatedCostCategory.findIndex((r) => r.rowNumber === data.no);

                    if (index >= 0) {
                      updatedCostCategory[index] = { ...updatedCostCategory[index], [field]: newValue };
                    } else {
                      updatedCostCategory.push({ rowNumber: data.no, [field]: newValue });
                    }

                    skipEffectRef.current = true;

                    setFormData({ ...formData, costCategory: updatedCostCategory });

                    const updatedRowInput = { ...data, [field]: newValue };
                    const calculator = costCategoryCalculator[data.no];

                    let updatedRow = { ...updatedRowInput };
                    if (calculator) {
                      const newCalculated = calculator(
                        updatedRowInput,
                        { ...formData, costCategory: updatedCostCategory },
                        rowPricing,
                        componentPricing,
                        miscPricing,
                        lineFillPricing
                      );
                      updatedRow = { ...updatedRowInput, ...newCalculated };
                    }

                    setGridData((prev) => {
                      const newGrid = prev.map((r) => (r.no === data.no ? updatedRow : r));

                      // Section 2: Sum for rows 15–28
                      let section2Contingency = 0;
                      let section2Base = 0;
                      let section2Total = 0;

                      for (const row of newGrid) {
                        if (row.no >= 15 && row.no <= 28) {
                          section2Contingency += isNaN(parseFloat(row.contingencyCost))
                            ? 0
                            : parseFloat(row.contingencyCost);
                          section2Base += isNaN(parseFloat(row.baseCost)) ? 0 : parseFloat(row.baseCost);
                          section2Total += isNaN(parseFloat(row.totalPrice)) ? 0 : parseFloat(row.totalPrice);
                        }
                      }

                      const summary2Index = newGrid.findIndex((r) => r.no === 29);
                      if (summary2Index !== -1) {
                        newGrid[summary2Index] = {
                          ...newGrid[summary2Index],
                          contingencyCost: section2Contingency,
                          baseCost: section2Base,
                          totalPrice: section2Total,
                          isSummary: true,
                        };
                      }

                      return newGrid;
                    });
                  }
                }}
              />
            </Box>

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : saved ? 'Saved!' : 'Save'}
              </Button>
            </Box>
          </Paper>
        )}

        <Dialog open={noteDialogOpen} onClose={resetDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{isEditing ? 'Edit Note' : 'View Note'}</DialogTitle>
          <DialogContent>
            <TextField
              multiline
              rows={6}
              fullWidth
              variant="outlined"
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              InputProps={{ readOnly: !isEditing }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={resetDialog}>Cancel</Button>
            {isEditing && (
              <Button variant="contained" onClick={handleNoteSave}>
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default Materials;
